import type {WSEvent} from '@interfaces/alpinist/ws'

/** Documents the turn is waiting on, and how far along each one is. */
export type IndexingState = Readonly<Record<string, number | null>>

const NOTHING: IndexingState = {}

/**
 * What the turn is still waiting to read (AB#5992).
 *
 * A file above the sync-parse threshold is indexed by a worker, and the turn
 * holds until that finishes instead of answering that it cannot read the file.
 * At the observed throughput that is minutes, so the file says it is being read
 * and — once ingest knows how much work it is — how far along it is. A silent
 * pause reads as a hang, and a spinner with no number reads as a stuck one.
 *
 * Keyed by document id, because the state is rendered on each file's own card
 * and two attachments sharing a name are not the same document. The value is
 * null while the file is still being parsed and there is no honest percentage.
 *
 * Pure, so the rule is testable without mounting the message list: the state is
 * whatever the last relevant event said, and anything the agent produces ends
 * the wait, whichever of those arrives first.
 */
export function nextIndexingDocuments(current: IndexingState, event: WSEvent): IndexingState {
  if (event.kind === 'attachment.indexing') {
    const percent = event.payload.percent ?? {}
    return Object.fromEntries(event.payload.document_ids.map((id) => [id, percent[id] ?? null]))
  }

  const agentIsWorking =
    event.kind === 'agent.token' ||
    event.kind === 'tool.update' ||
    (event.kind === 'msg.final' && event.payload.role !== 'user')

  // Same object back when nothing changes, so the list does not re-render on
  // every streamed token.
  if (!agentIsWorking) return current
  return Object.keys(current).length === 0 ? current : NOTHING
}
